import { catchError, finalize, map } from 'rxjs/operators';
import { Injectable, Inject } from '@angular/core';
import { AccountPlan } from '../../model/account-plan';
import { DataService } from '../../../core/service/base/data.service';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { AutoCompleteSelectionItem } from '../../../core/component/autocomplete-remote-table/autocomplete-remote-table.component';
import { IAutoCompleteService } from '../../../core/component/autocomplete-remote/autocomplete-service.interface';

@Injectable()
export class AccountPlanService extends DataService<AccountPlan> implements IAutoCompleteService<AccountPlan, any> {
  constructor(client: HttpClient, @Inject(APP_CONFIG) config: IAppConfig, messageService: MessageService, progressBar: ProgressBarService) {
    super(client, config, messageService, AccountPlan, progressBar, 'account-plan');
  }

  getExtensiveList(): Observable<AccountPlan[]> {
    return this.client.get<AccountPlan[]>(`${this.endpoint}/full`).pipe(map(response => this.toModels(response)));
  }

  copy(id: string): Observable<string> {
    this.progressBar.start();
    super.add;
    return this.client.post<string>(`${this.endpoint}/copy/${id}`, {}).pipe(
      catchError(error => {
        this.notification.showError(error);
        throw error;
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  getSelectionItem(id: string): Observable<AutoCompleteSelectionItem<AccountPlan>> {
    if (!id) {
      return of(null);
    }
    return this.client.get<AccountPlan>(`${this.endpoint}/selectionItem/${id}`).pipe(
      map(item => this.createAutoCompleteSelectionItem(item)),
      catchError(e => {
        this.notification.showError(e);
        return [];
      })
    );
  }

  getSelection(filter: string, options?: any, page?: number, maxItems?: number): Observable<AutoCompleteSelectionItem<AccountPlan>[]> {
    const queryArgs = {
      filter: filter,
      maxItems: maxItems || 20,
    };
    if (options) queryArgs['options'] = options;
    if (page > 0) queryArgs['page'] = page;

    return this.client.post<AccountPlan[]>(`${this.endpoint}/selection`, queryArgs).pipe(
      map(data => data.map(item => this.createAutoCompleteSelectionItem(item))),
      catchError(e => {
        this.notification.showError(e);
        return [];
      })
    );
  }

  createAutoCompleteSelectionItem(data: AccountPlan): AutoCompleteSelectionItem<AccountPlan> {
    if (!data) {
      return new AutoCompleteSelectionItem('', null, null);
    }
    const name = `${data.name}`;
    const shortName = data.name;
    return new AutoCompleteSelectionItem(name, data.id, Object.assign(new AccountPlan(), data), shortName);
  }
}
